import type {
  WbsClient,
  ApiSubscriberInfoDtoAccountSingleView as SubscriberInfoDto,
} from '@bldsoft/cappie/api/wbs'
import type {
  AccountAccessToken,
  API,
  GenericApiResponse,
  ResetPasswordSession,
  Session,
  Subscriber,
  SubscriberRegistrationStatus,
} from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'

import { ensureOk, payload } from '@bldsoft/cappie/rest'
import { SubscriberGender } from '@setplex/tria-api'
import * as v from 'valibot'
import { toDate } from '../../tools'
import { FeaturesSchema } from './schemas'

function transformer(dto: SubscriberInfoDto): Subscriber {
  return {
    ...dto,
    name: dto.firstName || '',
    gender: {
      MALE: SubscriberGender.Male,
      FEMALE: SubscriberGender.Female,
      OTHER: SubscriberGender.Other,
      NOT_KNOWN: SubscriberGender.Unknown,
    }[dto.gender || 'NOT_KNOWN'],
    phone: dto.phoneNumber,
    birthday: toDate(dto.dateOfBirth),
  }
}

export function use(
  client: WbsClient,
  subscriber: API['subscriber'],
  _api: API,
  _defaults: AdapterDefaults
): void {
  // get subscriber info
  subscriber.getFx.use(async () => {
    const json = await payload(client.get.api.v3.account())
    return transformer(json)
  })

  subscriber.updateProfileSwitchLockedFx.use(async (profileSwitchLocked) => {
    await ensureOk(
      client.put.api.v3.account.profileSwitchLocked({
        body: {
          profileSwitchLocked,
        },
      })
    )
  })

  // Returns info about features
  subscriber.getFeaturesFx.use(async () => {
    const json = await payload(client.get.api.v3.features())
    return v.parse(FeaturesSchema, json)
  })

  // In-app subscriber registration by email
  subscriber.signUpFx.use(
    async ({ email, password, username }) =>
      (await payload(
        client.post.api.v3.subscribers.register({
          body: {
            email,
            password,
            username,
          },
        })
      )) as GenericApiResponse<{
        id: string
        time_value: number
        time_period: string
      }>['payload']
  )

  // In-app subscriber registration by phone
  subscriber.signUpByPhoneFx.use(
    async ({ phoneNumber }) =>
      (await payload(
        client.post.auth.phone.init({
          body: {
            phoneNumber,
          },
        })
      )) as GenericApiResponse<{
        id: string
        time_value: number
        time_period: string
      }>['payload']
  )

  // Get status of registration process by id
  subscriber.getRegistrationStatusFx.use(
    async ({ id }) =>
      (await payload(client.get.api.v3.subscribers.register(id))) as {
        status: SubscriberRegistrationStatus
      }
  )

  // Resend confirmation email
  subscriber.resendConfirmationEmailFx.use(async ({ id }) => {
    await ensureOk(
      client.post.api.v3.subscribers.register.resend({
        body: {
          id,
        },
      })
    )
  })

  // Resend confirmation phone
  subscriber.resendConfirmationPhoneFx.use(async ({ id }) => {
    await ensureOk(
      client.post.auth.phone.resend({
        body: {
          id,
        },
      })
    )
  })

  // Confirm registration code from email
  subscriber.confirmCodeRegistrationFx.use(
    async ({ id, code }) =>
      (await payload(
        client.post.api.v3.subscribers.register.confirm({
          body: {
            id,
            code,
          },
        })
      )) as { firstName: string; lastName: string; email: string }
  )

  // Confirm registration code from email
  subscriber.confirmCodeRegistrationByPhoneFx.use(
    async ({ id, code, rememberMe }) => {
      const ok = await ensureOk(
        client.post.auth.phone({
          body: {
            id,
            code,
            rememberMe,
            manualProfileManagement: true, // prevents automatic profile creation on the backend
          },
          headers: {
            'MW-Paid-Content-Allowed': 'true',
            'MW-InApp-Subscription-Payment-Supported': 'true',
          },
        })
      )
      return { payload: ok.payload as Session, code: ok.http.status }
    }
  )

  // Start reset password process, send email with reset code, returns session identifier for reset password operations
  subscriber.initResetPasswordFx.use(async ({ email }) => {
    const ok = (await payload(
      client.post.api.v3.account.password.initiate({
        body: {
          email,
        },
      })
    )) as ResetPasswordSession
    return ok
  })

  // Resend resetCode
  subscriber.resendResetPasswordCodeFx.use(async ({ id }) => {
    await ensureOk(
      client.post.api.v3.account.password.resend({
        body: {
          id,
        },
      })
    )
  })

  // Verify reset password code
  subscriber.verifyResetPasswordCodeFx.use(async ({ id, code }) => {
    await ensureOk(
      client.post.api.v3.account.password.verify({
        body: {
          id,
          resetCode: code,
        },
      })
    )
  })

  // Reset password, set a new one
  subscriber.setNewPasswordFx.use(async ({ id, code, password }) => {
    await ensureOk(
      client.post.api.v3.account.password.reset({
        body: {
          id,
          resetCode: code,
          password,
        },
      })
    )
  })

  subscriber.getAccountAccessTokenFx.use(
    async () =>
      (await payload(
        client.get.api.v3.account.accessToken()
      )) as AccountAccessToken
  )

  // update subscriber username
  subscriber.updateSubscriberUsernameFx.use(async ({ username }) => {
    await ensureOk(
      client.put.api.v3.account.username({
        body: {
          username,
        },
      })
    )
  })

  // update subscriber password
  subscriber.updateSubscriberPasswordFx.use(
    async ({ password, newPassword, logoutAfterPasswordChange }) => {
      await ensureOk(
        client.put.api.v3.account.password({
          body: {
            password,
            newPassword,
            logoutAfterPasswordChange,
          },
        })
      )
    }
  )
}
